import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

export type LeaseStatus = 'Active' | 'Ending soon' | 'Ended' | 'Eviction';
export type LedgerType = 'Rent' | 'Late fee' | 'Deposit' | 'Utility' | 'Payment' | 'Credit' | 'Adjustment';

export interface LedgerEntry {
  id: number;
  date: string;
  type: LedgerType;
  description: string;
  charge: number;   // adds to the balance owed
  payment: number;  // reduces the balance owed (rent payments, credits)
  balance?: number; // running balance after this entry — computed
}

export interface Lease {
  id: number;
  tenant_name: string;
  tenant_email: string;
  property_name: string;
  unit: string;
  start_date: string;
  end_date: string;
  monthly_rent: number;          // normalized-monthly figure — always populated so existing aggregations keep working
  deposit_held: number;
  status: LeaseStatus;
  late_fee_amount: number;
  late_fee_grace_days: number;
  ledger: LedgerEntry[];

  // Links this lease to a Revenue/Optimizer address (MOCK_ADDRESSES ids) so
  // the optimizer calendar can render its term as booked — a signed lease is
  // the legal agreement; the reservation is what actually blocks the
  // calendar. Undefined for the standalone leases with no matching address
  // in the demo cast (background volume, not shown on any calendar).
  address_id?: number;

  // Room-splitting — all optional, undefined means "whole unit, monthly" (unchanged behavior)
  space_id?: number;
  space_name?: string;
  cadence?: 'weekly' | 'monthly';  // undefined = monthly
  term_type?: 'fixed' | 'month_to_month';
  liability?: 'individual' | 'joint';
  weekly_rent?: number;             // populated alongside monthly_rent when cadence is weekly
}

export interface LedgerSummary {
  entries: LedgerEntry[];   // sorted oldest → newest, each with a running balance
  balance: number;          // current balance (>0 owed, <0 credit, 0 current)
  totalCharged: number;
  totalPaid: number;
}

const DUMMY_LEASES: Lease[] = [
  // Current — paid up
  {
    id: 1, tenant_name: 'Sarah Mitchell', tenant_email: 'sarah.mitchell@example.com',
    property_name: 'City View Condo', unit: 'Unit 1', start_date: '2026-03-15', end_date: '2027-03-14',
    monthly_rent: 2450, deposit_held: 2450, status: 'Active', late_fee_amount: 75, late_fee_grace_days: 5,
    ledger: [
      { id: 1, date: '2026-03-15', type: 'Deposit', description: 'Security deposit', charge: 2450, payment: 0 },
      { id: 2, date: '2026-03-15', type: 'Payment', description: 'Deposit paid · ACH', charge: 0, payment: 2450 },
      { id: 3, date: '2026-04-01', type: 'Rent', description: 'April rent', charge: 2450, payment: 0 },
      { id: 4, date: '2026-04-02', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2450 },
      { id: 5, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 2450, payment: 0 },
      { id: 6, date: '2026-05-02', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2450 },
      { id: 7, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 2450, payment: 0 },
      { id: 8, date: '2026-06-03', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2450 },
      { id: 9, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 2450, payment: 0 },
      { id: 10, date: '2026-07-02', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2450 },
      { id: 11, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 2450, payment: 0 },
      { id: 12, date: '2026-08-02', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2450 },
    ],
  },
  // Delinquent — two months behind with auto late fees
  {
    id: 2, tenant_name: 'James Anderson', tenant_email: 'james.a@example.com',
    property_name: 'Mountain Retreat', unit: 'Unit 2', start_date: '2026-02-01', end_date: '2027-01-31',
    monthly_rent: 2400, deposit_held: 2400, status: 'Active', late_fee_amount: 75, late_fee_grace_days: 5,
    ledger: [
      { id: 1, date: '2026-02-01', type: 'Deposit', description: 'Security deposit', charge: 2400, payment: 0 },
      { id: 2, date: '2026-02-01', type: 'Payment', description: 'Deposit paid · Card', charge: 0, payment: 2400 },
      { id: 3, date: '2026-02-01', type: 'Rent', description: 'February rent', charge: 2400, payment: 0 },
      { id: 4, date: '2026-02-03', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2400 },
      { id: 5, date: '2026-03-01', type: 'Rent', description: 'March rent', charge: 2400, payment: 0 },
      { id: 6, date: '2026-03-02', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2400 },
      { id: 7, date: '2026-04-01', type: 'Rent', description: 'April rent', charge: 2400, payment: 0 },
      { id: 8, date: '2026-04-05', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2400 },
      { id: 9, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 2400, payment: 0 },
      { id: 10, date: '2026-05-06', type: 'Late fee', description: 'Late fee · auto-applied (5-day grace)', charge: 75, payment: 0 },
      { id: 11, date: '2026-05-09', type: 'Payment', description: 'Rent + late fee payment · ACH', charge: 0, payment: 2475 },
      { id: 12, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 2400, payment: 0 },
      { id: 13, date: '2026-06-03', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2400 },
      { id: 14, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 2400, payment: 0 },
      { id: 15, date: '2026-07-06', type: 'Late fee', description: 'Late fee · auto-applied (5-day grace)', charge: 75, payment: 0 },
      { id: 16, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 2400, payment: 0 },
    ],
  },
  // Current — lease ending soon
  {
    id: 3, tenant_name: 'Emily Rodriguez', tenant_email: 'emily.r@example.com',
    property_name: 'Secluded Cabin', unit: 'Unit 3', start_date: '2025-09-01', end_date: '2026-08-31',
    monthly_rent: 2350, deposit_held: 2350, status: 'Ending soon', late_fee_amount: 75, late_fee_grace_days: 5,
    ledger: [
      { id: 1, date: '2026-04-01', type: 'Rent', description: 'April rent', charge: 2350, payment: 0 },
      { id: 2, date: '2026-04-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2350 },
      { id: 3, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 2350, payment: 0 },
      { id: 4, date: '2026-05-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2350 },
      { id: 5, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 2350, payment: 0 },
      { id: 6, date: '2026-06-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2350 },
      { id: 7, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 2350, payment: 0 },
      { id: 8, date: '2026-07-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2350 },
      { id: 9, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 2350, payment: 0 },
      { id: 10, date: '2026-08-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2350 },
    ],
  },
  // Paid ahead — credit balance. Whole-unit lease for Cadence Cottage
  // (address_id 990003) — its signed term is what reserves that listing's
  // calendar for the optimizer demo (see rental-strategy.service.ts's
  // 'longer' window covering the rest of the calendar).
  {
    id: 4, tenant_name: 'Olivia Bennett', tenant_email: 'olivia.b@example.com',
    property_name: 'Cadence Cottage', unit: 'Whole home', start_date: '2026-01-01', end_date: '2026-12-31',
    monthly_rent: 2750, deposit_held: 2750, status: 'Active', late_fee_amount: 75, late_fee_grace_days: 5,
    address_id: 990003,
    ledger: [
      { id: 1, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 2750, payment: 0 },
      { id: 2, date: '2026-05-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2750 },
      { id: 3, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 2750, payment: 0 },
      { id: 4, date: '2026-06-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 2750 },
      { id: 5, date: '2026-06-25', type: 'Payment', description: 'July rent prepaid · ACH', charge: 0, payment: 2750 },
      { id: 6, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 2750, payment: 0 },
      { id: 7, date: '2026-07-25', type: 'Payment', description: 'August rent prepaid · ACH', charge: 0, payment: 2750 },
    ],
  },
  // --- Room-splitting demo: Palmetto House (unit 9) — weekly room, individual, month-to-month ---
  {
    id: 5, tenant_name: 'Marcus Whitfield', tenant_email: 'marcus.whitfield@example.com',
    property_name: 'Palmetto House', unit: 'Room A', start_date: '2026-05-04', end_date: '2027-05-03',
    monthly_rent: 975, deposit_held: 225, status: 'Active', late_fee_amount: 25, late_fee_grace_days: 3,
    address_id: 990001,
    space_id: 901, space_name: 'Room A', cadence: 'weekly', term_type: 'month_to_month', liability: 'individual', weekly_rent: 225,
    ledger: [
      { id: 1, date: '2026-05-04', type: 'Deposit', description: 'Security deposit', charge: 225, payment: 0 },
      { id: 2, date: '2026-05-04', type: 'Payment', description: 'Deposit paid · Card', charge: 0, payment: 225 },
      { id: 3, date: '2026-06-08', type: 'Rent', description: 'Weekly rent · wk of Jun 8', charge: 225, payment: 0 },
      { id: 4, date: '2026-06-08', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 5, date: '2026-06-15', type: 'Rent', description: 'Weekly rent · wk of Jun 15', charge: 225, payment: 0 },
      { id: 6, date: '2026-06-15', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 7, date: '2026-06-22', type: 'Rent', description: 'Weekly rent · wk of Jun 22', charge: 225, payment: 0 },
      { id: 8, date: '2026-06-22', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 9, date: '2026-06-29', type: 'Rent', description: 'Weekly rent · wk of Jun 29', charge: 225, payment: 0 },
      { id: 10, date: '2026-06-29', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 11, date: '2026-07-06', type: 'Rent', description: 'Weekly rent · wk of Jul 6', charge: 225, payment: 0 },
      { id: 12, date: '2026-07-08', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 13, date: '2026-07-13', type: 'Rent', description: 'Weekly rent · wk of Jul 13', charge: 225, payment: 0 },
      { id: 14, date: '2026-07-13', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 15, date: '2026-07-20', type: 'Rent', description: 'Weekly rent · wk of Jul 20', charge: 225, payment: 0 },
      { id: 16, date: '2026-07-20', type: 'Payment', description: 'Rent payment · Card', charge: 0, payment: 225 },
      { id: 17, date: '2026-07-27', type: 'Rent', description: 'Weekly rent · wk of Jul 27', charge: 225, payment: 0 },
    ],
  },
  // --- Room-splitting demo: Palmetto House (unit 9) — monthly room, individual, fixed term ---
  {
    id: 6, tenant_name: 'Keisha Boone', tenant_email: 'keisha.boone@example.com',
    property_name: 'Palmetto House', unit: 'Room B', start_date: '2026-02-01', end_date: '2027-01-31',
    monthly_rent: 780, deposit_held: 780, status: 'Active', late_fee_amount: 50, late_fee_grace_days: 5,
    address_id: 990001,
    space_id: 902, space_name: 'Room B', cadence: 'monthly', term_type: 'fixed', liability: 'individual',
    ledger: [
      { id: 1, date: '2026-02-01', type: 'Deposit', description: 'Security deposit', charge: 780, payment: 0 },
      { id: 2, date: '2026-02-01', type: 'Payment', description: 'Deposit paid · ACH', charge: 0, payment: 780 },
      { id: 3, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 780, payment: 0 },
      { id: 4, date: '2026-05-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 780 },
      { id: 5, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 780, payment: 0 },
      { id: 6, date: '2026-06-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 780 },
      { id: 7, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 780, payment: 0 },
      { id: 8, date: '2026-07-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 780 },
      { id: 9, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 780, payment: 0 },
      { id: 10, date: '2026-08-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 780 },
    ],
  },
  // --- Room-splitting demo: Palmetto House (unit 9) — monthly room, individual, fixed term ---
  {
    id: 7, tenant_name: 'Felix Ostrander', tenant_email: 'felix.ostrander@example.com',
    property_name: 'Palmetto House', unit: 'Room C', start_date: '2026-01-15', end_date: '2027-01-14',
    monthly_rent: 825, deposit_held: 825, status: 'Active', late_fee_amount: 50, late_fee_grace_days: 5,
    address_id: 990001,
    space_id: 903, space_name: 'Room C', cadence: 'monthly', term_type: 'fixed', liability: 'individual',
    ledger: [
      { id: 1, date: '2026-01-15', type: 'Deposit', description: 'Security deposit', charge: 825, payment: 0 },
      { id: 2, date: '2026-01-15', type: 'Payment', description: 'Deposit paid · ACH', charge: 0, payment: 825 },
      { id: 3, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 825, payment: 0 },
      { id: 4, date: '2026-05-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 825 },
      { id: 5, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 825, payment: 0 },
      { id: 6, date: '2026-06-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 825 },
      { id: 7, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 825, payment: 0 },
      { id: 8, date: '2026-07-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 825 },
      { id: 9, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 825, payment: 0 },
      { id: 10, date: '2026-08-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 825 },
    ],
  },
  // --- Room-splitting demo: Ferncreek Rooming House (unit 10) — paid up ---
  {
    id: 8, tenant_name: 'Renata Cho', tenant_email: 'renata.cho@example.com',
    property_name: 'Ferncreek Rooming House', unit: 'Room 1', start_date: '2026-05-01', end_date: '2026-10-31',
    monthly_rent: 700, deposit_held: 700, status: 'Active', late_fee_amount: 40, late_fee_grace_days: 5,
    address_id: 990002,
    space_id: 1001, space_name: 'Room 1', cadence: 'monthly', term_type: 'fixed', liability: 'individual',
    ledger: [
      { id: 1, date: '2026-05-01', type: 'Deposit', description: 'Security deposit', charge: 700, payment: 0 },
      { id: 2, date: '2026-05-01', type: 'Payment', description: 'Deposit paid · ACH', charge: 0, payment: 700 },
      { id: 3, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 700, payment: 0 },
      { id: 4, date: '2026-05-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 700 },
      { id: 5, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 700, payment: 0 },
      { id: 6, date: '2026-06-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 700 },
      { id: 7, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 700, payment: 0 },
      { id: 8, date: '2026-07-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 700 },
      { id: 9, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 700, payment: 0 },
      { id: 10, date: '2026-08-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 700 },
    ],
  },
  // --- Room-splitting demo: Ferncreek Rooming House (unit 10) — overdue, escalates derivedState ---
  {
    id: 9, tenant_name: 'Dwayne Pruitt', tenant_email: 'dwayne.pruitt@example.com',
    property_name: 'Ferncreek Rooming House', unit: 'Room 2', start_date: '2026-04-01', end_date: '2027-03-31',
    monthly_rent: 675, deposit_held: 675, status: 'Active', late_fee_amount: 40, late_fee_grace_days: 5,
    address_id: 990002,
    space_id: 1002, space_name: 'Room 2', cadence: 'monthly', term_type: 'fixed', liability: 'individual',
    ledger: [
      { id: 1, date: '2026-04-01', type: 'Deposit', description: 'Security deposit', charge: 675, payment: 0 },
      { id: 2, date: '2026-04-01', type: 'Payment', description: 'Deposit paid · ACH', charge: 0, payment: 675 },
      { id: 3, date: '2026-04-01', type: 'Rent', description: 'April rent', charge: 675, payment: 0 },
      { id: 4, date: '2026-04-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 675 },
      { id: 5, date: '2026-05-01', type: 'Rent', description: 'May rent', charge: 675, payment: 0 },
      { id: 6, date: '2026-05-01', type: 'Payment', description: 'Rent payment · ACH', charge: 0, payment: 675 },
      { id: 7, date: '2026-06-01', type: 'Rent', description: 'June rent', charge: 675, payment: 0 },
      { id: 8, date: '2026-06-06', type: 'Late fee', description: 'Late fee · auto-applied (5-day grace)', charge: 40, payment: 0 },
      { id: 9, date: '2026-07-01', type: 'Rent', description: 'July rent', charge: 675, payment: 0 },
      { id: 10, date: '2026-07-06', type: 'Late fee', description: 'Late fee · auto-applied (5-day grace)', charge: 40, payment: 0 },
      { id: 11, date: '2026-08-01', type: 'Rent', description: 'August rent', charge: 675, payment: 0 },
    ],
  },
];

@Injectable({
  providedIn: 'root'
})
export class LeaseService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  // The leasing backend isn't live yet, so a real account has no leases — showing the
  // demo world's tenants there would be fabricating their data. The demo account is
  // the only place the sample ledgers belong.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  async getLeases(): Promise<Lease[]> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_LEASES : []; }
    return firstValueFrom(this.http.get<Lease[]>(`${environment.api_url}api/v1/customer/leases`));
  }

  async getLease(id: number): Promise<Lease | undefined> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_LEASES.find(l => l.id === id) : undefined; }
    return firstValueFrom(this.http.get<Lease>(`${environment.api_url}api/v1/customer/leases/${id}`));
  }

  async getLeasesForTenant(email: string): Promise<Lease[]> {
    if (!email) { return []; }
    const leases = await this.getLeases();
    return leases.filter(l => l.tenant_email.toLowerCase() === email.toLowerCase());
  }

  // Active/current leases for a Revenue/Optimizer address — used to render a
  // signed lease's term as booked on the optimizer calendar (a lease is the
  // legal agreement; the reservation is what actually blocks the calendar).
  // "Ended"/"Eviction" leases are excluded — they no longer hold the calendar.
  // Room leases (space_id set) are excluded too: one rented room in a by-room
  // unit doesn't reserve the whole property's calendar.
  async getLeasesForAddress(addressId: number): Promise<Lease[]> {
    if (this.useDummyData) {
      if (!this.showsDummyData) { return []; }
      return DUMMY_LEASES.filter(l => l.address_id === addressId && l.space_id === undefined && l.status !== 'Ended' && l.status !== 'Eviction');
    }
    return firstValueFrom(this.http.get<Lease[]>(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/leases`));
  }

  // Builds the running-balance statement for a lease.
  computeLedger(lease: Lease): LedgerSummary {
    const sorted = [...lease.ledger].sort((a, b) => {
      if (a.date === b.date) { return a.id - b.id; }
      return a.date < b.date ? -1 : 1;
    });
    let running = 0;
    let totalCharged = 0;
    let totalPaid = 0;
    const entries = sorted.map(e => {
      running += (e.charge || 0) - (e.payment || 0);
      totalCharged += e.charge || 0;
      totalPaid += e.payment || 0;
      return { ...e, balance: running };
    });
    return { entries, balance: running, totalCharged, totalPaid };
  }

  balanceFor(lease: Lease): number {
    return this.computeLedger(lease).balance;
  }

  async recordPayment(leaseId: number, amount: number, description = 'Payment recorded'): Promise<void> {
    const lease = DUMMY_LEASES.find(l => l.id === leaseId);
    if (!lease || amount <= 0) { return; }
    lease.ledger.push({
      id: this.nextEntryId(lease),
      date: new Date().toISOString().slice(0, 10),
      type: 'Payment',
      description,
      charge: 0,
      payment: amount,
    });
  }

  async addCharge(leaseId: number, type: LedgerType, amount: number, description: string): Promise<void> {
    const lease = DUMMY_LEASES.find(l => l.id === leaseId);
    if (!lease || amount <= 0) { return; }
    lease.ledger.push({
      id: this.nextEntryId(lease),
      date: new Date().toISOString().slice(0, 10),
      type,
      description,
      charge: amount,
      payment: 0,
    });
  }

  async assessLateFee(leaseId: number): Promise<void> {
    const lease = DUMMY_LEASES.find(l => l.id === leaseId);
    if (!lease) { return; }
    await this.addCharge(leaseId, 'Late fee', lease.late_fee_amount, `Late fee · ${lease.late_fee_amount} flat`);
  }

  private nextEntryId(lease: Lease): number {
    return Math.max(0, ...lease.ledger.map(e => e.id)) + 1;
  }

  statusColor(status: LeaseStatus): string {
    switch (status) {
      case 'Active': return '#2E7D32';
      case 'Ending soon': return '#F9A825';
      case 'Eviction': return '#C62828';
      case 'Ended': return '#999';
      default: return '#1E2125';
    }
  }

  // Red when money is owed, green when current or in credit.
  balanceColor(balance: number): string {
    if (balance > 0) { return '#C62828'; }
    if (balance < 0) { return '#2E7D32'; }
    return '#2E7D32';
  }

  balanceLabel(balance: number): string {
    if (balance > 0) { return `$${  balance.toLocaleString('en-US')  } owed`; }
    if (balance < 0) { return `$${  Math.abs(balance).toLocaleString('en-US')  } credit`; }
    return 'Paid up';
  }
}
